/**
 * Scoring utilities for calculating and aggregating UX evaluation scores
 */

/**
 * Calculate StepScore for a single evaluation
 * @param {Object} evaluation - Evaluation object with dimensions
 * @returns {number|null} - Average score (0-5) or null if all N/A
 */
export function calculateStepScore(evaluation) {
  if (!evaluation || !evaluation.dimensions) return null;
  
  const scores = Object.values(evaluation.dimensions)
    .filter(score => score !== 'N/A' && score !== null && score !== undefined)
    .map(score => Number(score));
  
  if (scores.length === 0) return null; // All N/A
  
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

/**
 * Calculate StepScoreFinal (average of all designers' StepScores)
 * @param {Array} evaluations - Array of evaluation objects
 * @returns {number|null} - Average StepScore (0-5) or null if no valid scores
 */
export function calculateStepScoreFinal(evaluations) {
  if (!evaluations || evaluations.length === 0) return null;
  
  const stepScores = evaluations
    .map(evaluation => calculateStepScore(evaluation))
    .filter(score => score !== null);
  
  if (stepScores.length === 0) return null;
  
  return stepScores.reduce((sum, score) => sum + score, 0) / stepScores.length;
}

/**
 * Calculate standard deviation (variance) across designers' StepScores
 * @param {Array} evaluations - Array of evaluation objects
 * @returns {number} - Standard deviation (0 if < 2 evaluations)
 */
export function calculateStepScoreVariance(evaluations) {
  if (!evaluations || evaluations.length === 0) return 0;
  
  const stepScores = evaluations
    .map(evaluation => calculateStepScore(evaluation))
    .filter(score => score !== null);
  
  if (stepScores.length < 2) return 0;
  
  const mean = stepScores.reduce((sum, score) => sum + score, 0) / stepScores.length;
  const squaredDiffs = stepScores.map(score => Math.pow(score - mean, 2));
  const variance = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / stepScores.length;
  
  return Math.sqrt(variance); // Standard deviation
}

/**
 * Convert StepScoreFinal to percentage (0-100)
 * @param {number|null} stepScoreFinal - StepScoreFinal value (0-5) or null
 * @returns {number|null} - Percentage (0-100) or null
 */
export function toPercentage(stepScoreFinal) {
  if (stepScoreFinal === null || stepScoreFinal === undefined) return null;
  return (stepScoreFinal / 5) * 100;
}

/**
 * Calculate JourneyScore (average of all StepScoreFinal values)
 * @param {Array} stepScores - Array of StepScoreFinal values
 * @returns {number|null} - JourneyScore as percentage (0-100) or null
 */
export function calculateJourneyScore(stepScores) {
  if (!stepScores || stepScores.length === 0) return null;
  
  const validScores = stepScores.filter(score => score !== null && score !== undefined);
  if (validScores.length === 0) return null;
  
  const sum = validScores.reduce((acc, score) => acc + score, 0);
  return (sum / validScores.length / 5) * 100; // Convert to percentage
}

/**
 * Check if variance exceeds threshold (high variance indicator)
 * @param {number} variance - Standard deviation value
 * @param {number} threshold - Variance threshold (default: 1.0)
 * @returns {boolean} - True if variance exceeds threshold
 */
export function hasHighVariance(variance, threshold = 1.0) {
  return variance > threshold;
}

/**
 * Get aggregated step score data
 * @param {Array} evaluations - Array of evaluation objects
 * @returns {Object} - Aggregated score data
 */
export function getAggregatedStepScore(evaluations) {
  const stepScoreFinal = calculateStepScoreFinal(evaluations);
  const stepScoreFinalPercentage = toPercentage(stepScoreFinal);
  const stepScoreVariance = calculateStepScoreVariance(evaluations);
  const hasHighVarianceFlag = hasHighVariance(stepScoreVariance);
  
  return {
    stepScoreFinal,
    stepScoreFinalPercentage,
    stepScoreVariance,
    hasHighVariance: hasHighVarianceFlag,
    evaluationCount: evaluations ? evaluations.length : 0
  };
}

